import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured, supabase } from "./lib/supabaseClient";

const SupabaseAuthCtx = createContext(null);

export function SupabaseAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);

  const refreshProfile = useCallback(async (userId) => {
    if (!supabase || !userId) {
      setProfile(null);
      return null;
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("id, username, role, node_id, borgo_id, preferred_language")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      console.warn("profiles:", error.message);
      setProfile(null);
      return null;
    }
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;

    const sessionTimeout = window.setTimeout(() => {
      if (mounted) setLoading(false);
    }, 5000);

    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        if (!mounted) return;
        setSession(s);
        if (s?.user?.id) refreshProfile(s.user.id);
        setLoading(false);
      })
      .catch(() => {
        if (mounted) setLoading(false);
      })
      .finally(() => {
        window.clearTimeout(sessionTimeout);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user?.id) refreshProfile(s.user.id);
      else setProfile(null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      window.clearTimeout(sessionTimeout);
      subscription.unsubscribe();
    };
  }, [refreshProfile]);

  const signInWithPassword = useCallback(async (email, password) => {
    if (!supabase) throw new Error("Supabase non configurato");
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      configured: isSupabaseConfigured,
      session,
      user: session?.user ?? null,
      profile,
      loading,
      signInWithPassword,
      signOut,
      refreshProfile,
    }),
    [session, profile, loading, signInWithPassword, signOut, refreshProfile]
  );

  return <SupabaseAuthCtx.Provider value={value}>{children}</SupabaseAuthCtx.Provider>;
}

const FALLBACK = {
  configured: false,
  session: null,
  user: null,
  profile: null,
  loading: false,
  signInWithPassword: async () => {
    throw new Error("Supabase non configurato");
  },
  signOut: async () => {},
  refreshProfile: async () => null,
};

export function useSupabaseAuth() {
  return useContext(SupabaseAuthCtx) ?? FALLBACK;
}
